import { type ReactNode, memo } from 'react';
import { Badge, Button, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { MobilePageLayout } from '@/components/layout/MobilePageLayout';
import { LoadingState } from '@/components/common/loading';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import { BrandLogo } from '@/components/common/BrandLogo';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import type { UseInvitationFlowReturn } from '@/hooks/useInvitationFlow';
import type { GetInvitationByTokenResponse } from '@/types/api/responses';
import { InvitationErrorState } from './InvitationErrorState';
import { InvitationSuccessContent } from './InvitationSuccessContent';
import { ProfileStep } from './ProfileStep';
import { InviterCard } from './InviterCard';
import styles from '../InvitationPage.module.css';

interface InvitationMobileViewProps {
  flow: UseInvitationFlowReturn;
}

interface MobileDetailsContentProps {
  invitation: GetInvitationByTokenResponse;
  isAccepting: boolean;
  isDeclining: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const CHECK_ICON_SIZE = 18;
const DECLINED_ICON_SIZE = 48;

const MobileDetailsContent = memo(function MobileDetailsContent({
  invitation,
  isAccepting,
  isDeclining,
  onAccept,
  onDecline,
}: MobileDetailsContentProps): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <h1 className={styles.mobileHeading}>
        {t('invitation.heading')}{' '}
        <span className={styles.brandAccent}>{invitation.workspaceName}</span>
      </h1>
      <p className={styles.mobileSubtitle}>{t('invitation.description')}</p>

      <InviterCard
        inviterName={invitation.inviterName}
        inviterRole={invitation.inviterRole}
        memberCount={invitation.memberCount}
      />

      <div className={styles.mobileDetailCard}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>
            {t('invitation.details.workspace')}
          </span>
          <span className={styles.detailValue}>{invitation.workspaceName}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>
            {t('invitation.details.yourRole')}
          </span>
          <Badge variant="light" size="sm">
            {invitation.assignedRole}
          </Badge>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>
            {t('invitation.details.email')}
          </span>
          <span className={styles.detailValue}>{invitation.email}</span>
        </div>
      </div>

      <div className={styles.buttonGroup}>
        <Button
          size="md"
          fullWidth
          leftSection={<IconCheck size={CHECK_ICON_SIZE} />}
          loading={isAccepting}
          disabled={isDeclining}
          onClick={onAccept}
        >
          {isAccepting
            ? t('invitation.actions.accepting')
            : t('invitation.actions.accept')}
        </Button>
        <Button
          size="md"
          fullWidth
          variant="subtle"
          loading={isDeclining}
          disabled={isAccepting}
          onClick={onDecline}
        >
          {isDeclining
            ? t('invitation.actions.declining')
            : t('invitation.actions.decline')}
        </Button>
      </div>
    </>
  );
});

export const InvitationMobileView = memo(function InvitationMobileView({
  flow,
}: InvitationMobileViewProps): ReactNode {
  const { t } = useTranslation();

  const renderContent = (): ReactNode => {
    switch (flow.step) {
      case 'loading':
        return <LoadingState centered text={t('invitation.loading')} />;

      case 'details':
        if (!flow.invitation) return null;
        return (
          <MobileDetailsContent
            invitation={flow.invitation}
            isAccepting={flow.isAccepting}
            isDeclining={flow.isDeclining}
            onAccept={flow.handleAccept}
            onDecline={flow.handleDecline}
          />
        );

      case 'profile':
        return <ProfileStep email={flow.invitation?.email ?? ''} />;

      case 'success':
        if (!flow.acceptResult) return null;
        return <InvitationSuccessContent acceptResult={flow.acceptResult} />;

      case 'declined':
        return (
          <div className={styles.declinedWrapper}>
            <div className={styles.declinedIcon}>
              <IconX size={DECLINED_ICON_SIZE} />
            </div>
            <Text size="lg" fw={FONT_WEIGHT_SEMIBOLD}>
              {t('invitation.declined.title')}
            </Text>
            <Text c="dimmed" size="sm">
              {t('invitation.declined.description')}
            </Text>
          </div>
        );

      case 'error':
        if (!flow.errorType) return null;
        return (
          <InvitationErrorState
            errorType={flow.errorType}
            onRetry={flow.handleRetry}
            onLogout={flow.handleLogout}
          />
        );

      default:
        return null;
    }
  };

  return (
    <MobilePageLayout
      logo={<BrandLogo size="sm" linkToHome />}
      actions={<OnboardingSettingsBar />}
    >
      {renderContent()}
    </MobilePageLayout>
  );
});
